import '@xyflow/react/dist/style.css';

import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  useReactFlow,
} from '@xyflow/react';
import { useCallback, useRef } from 'react';
import ResourceGroupNode from './NodeResourceGroup';
import SubnetNode from './NodeSubnet';
import VNetNode from './NodeVNet';
import VirtualMachineNode from './NodeVirtualMachine';
import ManagedDiskNode from './NodeManagedDisk';
import StorageAccountNode from './NodeStorageAccount';
import NSGNode from './NodeNSG';
import RouteTableNode from './NodeRouteTable';
import KeyVaultNode from './NodeKeyVault';
import AppServiceNode from './NodeAppService';
import LoadBalancerNode from './NodeLoadBalancer';

const nodeTypes = {
  'resource-group': ResourceGroupNode,
  vnet: VNetNode,
  subnet: SubnetNode,
  'virtual-machine': VirtualMachineNode,
  'managed-disk': ManagedDiskNode,
  'storage-account': StorageAccountNode,
  nsg: NSGNode,
  'route-table': RouteTableNode,
  'key-vault': KeyVaultNode,
  'app-service': AppServiceNode,
  'load-balancer': LoadBalancerNode,
};

function CanvasInner({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeSelect,
  onAddNode,
}) {
  const { screenToFlowPosition } = useReactFlow();
  const wrapperRef = useRef(null);
  const nodeCounter = useRef(0);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData('application/reactflow');
      if (!raw) return;

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return;
      }

      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      nodeCounter.current += 1;

      // Detect if dropped inside an existing node to auto-set parent
      let parentId = undefined;
      let extent = undefined;
      let nodePosition = position;

      if (parsed.type === 'vnet') {
        const rgParent = nodes.find((n) => {
          if (n.type !== 'resource-group') return false;
          const w = n.style?.width || 220;
          const h = n.style?.height || 120;
          return (
            position.x >= n.position.x &&
            position.x <= n.position.x + w &&
            position.y >= n.position.y &&
            position.y <= n.position.y + h
          );
        });
        if (rgParent) {
          parentId = rgParent.id;
          extent = 'parent';
          nodePosition = {
            x: position.x - rgParent.position.x,
            y: position.y - rgParent.position.y,
          };
        }
      } else if (parsed.type === 'subnet') {
        const vnetParent = nodes.find((n) => {
          if (n.type !== 'vnet') return false;
          const w = n.style?.width || 220;
          const h = n.style?.height || 120;
          return (
            position.x >= n.position.x &&
            position.x <= n.position.x + w &&
            position.y >= n.position.y &&
            position.y <= n.position.y + h
          );
        });
        if (vnetParent) {
          parentId = vnetParent.id;
          extent = 'parent';
          nodePosition = {
            x: position.x - vnetParent.position.x,
            y: position.y - vnetParent.position.y,
          };
        }
      } else if (parsed.type === 'virtual-machine' || parsed.type === 'managed-disk' || parsed.type === 'storage-account' || parsed.type === 'nsg' || parsed.type === 'route-table' || parsed.type === 'key-vault' || parsed.type === 'app-service' || parsed.type === 'load-balancer') {
        const rgParent = nodes.find((n) => {
          if (n.type !== 'resource-group') return false;
          const w = n.style?.width || 220;
          const h = n.style?.height || 120;
          return (
            position.x >= n.position.x &&
            position.x <= n.position.x + w &&
            position.y >= n.position.y &&
            position.y <= n.position.y + h
          );
        });
        if (rgParent) {
          parentId = rgParent.id;
          extent = 'parent';
          nodePosition = {
            x: position.x - rgParent.position.x,
            y: position.y - rgParent.position.y,
          };
        }
      }

      const newNode = {
        id: `${parsed.type}-${Date.now()}-${nodeCounter.current}`,
        type: parsed.type,
        position: nodePosition,
        data: parsed.data,
        style: parsed.style
          ? { width: parsed.style.width, height: parsed.style.height }
          : undefined,
        parentId,
        extent,
      };
      onAddNode(newNode);
    },
    [screenToFlowPosition, onAddNode, nodes],
  );

  return (
    <div ref={wrapperRef} className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={(_e, node) => onNodeSelect(node.id)}
        onPaneClick={() => onNodeSelect(null)}
        onDrop={onDrop}
        onDragOver={onDragOver}
        isValidConnection={(connection) => {
          const source = nodes.find((n) => n.id === connection.source);
          const target = nodes.find((n) => n.id === connection.target);
          if (!source || !target) return false;
          if (source.id === target.id) return false;
          const st = source.data?.type;
          const tt = target.data?.type;
          const valid = [
            ['resource-group', 'vnet'],
            ['vnet', 'subnet'],
            ['vnet', 'vnet'],
            ['subnet', 'virtual-machine'],
            ['subnet', 'nsg'],
            ['subnet', 'route-table'],
            ['nsg', 'subnet'],
            ['route-table', 'subnet'],
            ['resource-group', 'virtual-machine'],
            ['resource-group', 'managed-disk'],
            ['resource-group', 'storage-account'],
            ['resource-group', 'nsg'],
            ['resource-group', 'route-table'],
            ['resource-group', 'key-vault'],
            ['resource-group', 'app-service'],
            ['resource-group', 'load-balancer'],
          ];
          return valid.some(([s, t]) => st === s && tt === t);
        }}
        nodeTypes={nodeTypes}
        fitView
        deleteKeyCode="Delete"
        className="bg-gray-50 dark:bg-gray-900"
      >
        <MiniMap
          nodeColor={(node) => {
            if (node.type === 'resource-group') return '#5eead4';
            if (node.type === 'vnet') return '#93c5fd';
            return '#5eead4';
          }}
          className="rounded-md border border-gray-200 dark:border-gray-700 shadow-sm"
        />
        <Controls className="rounded-md border border-gray-200 dark:border-gray-700 shadow-sm" />
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e5e7eb" />
      </ReactFlow>
    </div>
  );
}

export default function Canvas(props) {
  return <CanvasInner {...props} />;
}
